import { useCallback } from 'react';
import { createDefaultDisplaySettings, type CharacterHomeEffects, type DisplaySettingId, type DisplaySettings } from '@sorrem/shared';
import { useAccountSetting } from './use-account-setting';

const DEFAULT_DISPLAY_SETTINGS: Readonly<DisplaySettings> = createDefaultDisplaySettings();

interface DisplaySettingsState {
    /** How much of Character Home moves: none, its 2D effects, or all; all until settings load. */
    characterHomeEffects: CharacterHomeEffects;
    /** Damage number and essence bar settings; the defaults until settings load. */
    displaySettings: Readonly<DisplaySettings>;
    /** Saves the Character Home effects level to the account (applied optimistically). */
    setCharacterHomeEffects: (effects: CharacterHomeEffects) => void;
    /** Saves one display setting to the account (applied optimistically). */
    setDisplaySetting: (id: DisplaySettingId, enabled: boolean) => void;
}

/**
 * The account's display settings, shown in the settings pop-over (#203).
 * Account settings, so they follow the player from one browser to the next.
 */
export function useDisplaySettings(): DisplaySettingsState {
    const { value: characterHomeEffects, set: setCharacterHomeEffects } = useAccountSetting('characterHomeEffects', 'all');
    const { value: displaySettings, set: setDisplaySettings } = useAccountSetting('displaySettings', DEFAULT_DISPLAY_SETTINGS);

    const setDisplaySetting = useCallback((id: DisplaySettingId, enabled: boolean): void => {
        setDisplaySettings((current) => (current[id] === enabled ? current : { ...current, [id]: enabled }));
    }, [setDisplaySettings]);

    return { characterHomeEffects, displaySettings, setCharacterHomeEffects, setDisplaySetting };
}
